import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'
import { formatMatrixDisplayName } from '@/modules/chat-matrix-fast/services/matrixUserId'

export const DELETED_MESSAGE_TEXT = 'Сообщение было удалено'

export const MATRIX_HIDDEN_LOCALPARTS = ['matrix-admin']

/**
 * Фоновому sync достаточно короткого хвоста комнаты для unread и превью.
 * Полная история загружается отдельно только при открытии конкретного чата.
 */
export const MATRIX_INITIAL_SYNC_LIMIT = 5

export function isMatrixHiddenUser(userId: string): boolean {
  const localpart = userId.split(':')[0].slice(1)
  return MATRIX_HIDDEN_LOCALPARTS.includes(localpart)
}

/** Первые N чатов в core-списке считаются «новыми» без сообщений */
export const NEW_CHATS_HEAD_COUNT = 5

export const MESSAGE_UPDATE_DELAY = 100

export const REACTION_PICKER_HIDE_DELAY = 200

export const REACTION_TRIGGER_HIDE_DELAY = 500

export const CHAT_TYPES = {
  GROUP: 'group',
  CHANNEL: 'channel',
  WORKGROUP: 'workgroup',
  DIALOG: 'dialog',
  DIRECT: 'direct',
  EVENT: 'event',
  SPACE: 'space',
  FAVORITE: 'favorite',
} as const

/** Отображаемое имя чата «Избранное» / saved messages */
export const FAVORITE_CHAT_TITLE = 'Заметки'

const GENERIC_CHAT_TITLES = new Set(['неизвестный', 'unknown', 'empty room', 'empty_room', 'empty'])

/** Автоимя бэкенда для личного чата, который отдали как group. */
const GENERATED_MATRIX_CHAT_TITLE = /^matrix\s*чат\s*#\s*\d+$/i

type ChatTitleSource = {
  type?: string | null
  title?: string | null
  matrix_room_id?: string | null
  matrix_peer_id?: string | null
  dialog_member?: IChat['dialog_member']
  members?: IChat['members']
}

type EmployeeNameSource = {
  full_name?: string | null
  first_name?: string | null
  last_name?: string | null
  middle_name?: string | null
  user_name?: string | null
}

function employeeLikeDisplayName(person: EmployeeNameSource | null | undefined): string {
  if (!person) {
    return ''
  }

  const fromParts = [person.last_name, person.first_name, person.middle_name]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(' ')
    .trim()

  const fromFull = (person.full_name || '').trim()
  if (fromParts && fromFull) {
    return personNamePartCount(fromFull) > personNamePartCount(fromParts) ? fromFull : fromParts
  }

  return fromParts || fromFull
}

function dialogMemberDisplayName(member: IChat['dialog_member'] | null | undefined): string {
  return employeeLikeDisplayName(member)
}

/** Список чатов: фамилия + имя без отчества (как в dialog_member last/first). */
function dialogMemberListName(member: IChat['dialog_member'] | null | undefined): string {
  if (!member) {
    return ''
  }

  const short = [member.last_name, member.first_name]
    .filter((part): part is string => Boolean(part?.trim()))
    .map(part => part.trim())
    .join(' ')
    .trim()

  if (short) {
    return short
  }

  return (member.full_name || '').trim()
}

function personNamePartCount(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length
}

function dialogPeerNameFromMembers(chat: ChatTitleSource): string {
  const members = chat.members
  if (!members?.length) {
    return ''
  }

  const peerId = chat.dialog_member?.id ? String(chat.dialog_member.id) : ''
  const peerMxid = (chat.dialog_member?.matrix_user_id || chat.matrix_peer_id || '').trim()

  const named = members.map(member => {
    const rawUser = (member as { user?: unknown }).user
    const nested = rawUser && typeof rawUser === 'object'
      ? rawUser as EmployeeNameSource & { id?: string; uuid?: string; matrix_user_id?: string }
      : null
    const id = String(
      member.user_uuid
      || member.user_id
      || (typeof rawUser === 'string' || typeof rawUser === 'number' ? rawUser : '')
      || nested?.id
      || nested?.uuid
      || '',
    )
    const mxid = String(member.user_uuid || nested?.matrix_user_id || '').trim()
    const name = employeeLikeDisplayName(member) || employeeLikeDisplayName(nested)

    return { id, mxid, name }
  }).filter(item => item.name && !item.name.startsWith('@'))

  if (peerId) {
    const match = named.find(item => item.id === peerId)
    if (match?.name) {
      return match.name
    }
  }

  if (peerMxid) {
    const match = named.find(item => item.mxid === peerMxid)
    if (match?.name) {
      return match.name
    }
  }

  return named.length === 1 ? (named[0]?.name ?? '') : ''
}

/** Room id, empty room, «Неизвестный» — не человеческое имя чата. */
export function isGenericChatTitle(
  title: string | null | undefined,
  matrixRoomId?: string | null,
): boolean {
  const normalized = (title ?? '').trim()
  if (!normalized) {
    return true
  }

  if (GENERIC_CHAT_TITLES.has(normalized.toLowerCase())) {
    return true
  }

  if (GENERATED_MATRIX_CHAT_TITLE.test(normalized)) {
    return true
  }

  return isFallbackEventChatTitle({
    title: normalized,
    matrix_room_id: matrixRoomId ?? undefined,
  })
}

/** MXID, телефон, room id — не ФИО собеседника. */
export function isNonHumanDialogTitle(
  title: string | null | undefined,
  matrixRoomId?: string | null,
  peerMxid?: string | null,
): boolean {
  const normalized = (title ?? '').trim()
  if (!normalized) {
    return true
  }

  if (isGenericChatTitle(normalized, matrixRoomId)) {
    return true
  }

  if (normalized.startsWith('@')) {
    return true
  }

  const peer = peerMxid?.trim()
  if (peer) {
    if (normalized === peer || normalized === formatMatrixDisplayName(peer)) {
      return true
    }

    if (peer.startsWith('@')) {
      const localpart = peer.split(':')[0].slice(1)
      if (normalized === localpart) {
        return true
      }
    }
  }

  const digits = normalized.replace(/\D/g, '')
  return digits.length >= 10 && digits.length <= 15
}

/** ФИО: два слова или одно кириллическое. «Matrix чат #164» и username — нет. */
export function looksLikePersonName(value: string | null | undefined): boolean {
  const normalized = (value ?? '').trim()
  if (!normalized || normalized.startsWith('@') || normalized.startsWith('!')) {
    return false
  }

  if (GENERATED_MATRIX_CHAT_TITLE.test(normalized) || /#\d+/.test(normalized)) {
    return false
  }

  const letters = normalized.replace(/[^a-zA-Zа-яА-ЯёЁ]/g, '')
  if (letters.length < 2) {
    return false
  }

  const digits = normalized.replace(/\D/g, '')
  if (digits.length >= 10 && digits.length <= 15 && letters.length < 4) {
    return false
  }

  const words = normalized.split(/\s+/).filter(Boolean)
  const nameWords = words.filter(word => /[a-zA-Zа-яА-ЯёЁ]/.test(word) && !/\d/.test(word))
  if (nameWords.length >= 2) {
    return true
  }

  return nameWords.length === 1
    && words.length === 1
    && /[а-яА-ЯёЁ]/.test(normalized)
    && !/[a-zA-Z]/.test(normalized)
    && !/\d/.test(normalized)
}

function scoreDialogTitle(title: string, chat: ChatTitleSource): number {
  const trimmed = title.trim()
  if (!trimmed) {
    return 0
  }

  if (isNonHumanDialogTitle(
    trimmed,
    chat.matrix_room_id,
    chat.matrix_peer_id ?? chat.dialog_member?.matrix_user_id,
  )) {
    return 1
  }

  return looksLikePersonName(trimmed) ? 3 : 2
}

function titleCandidatesFromChat(chat: ChatTitleSource): string[] {
  const isDialog = chat.type === CHAT_TYPES.DIALOG || chat.type === CHAT_TYPES.DIRECT
  const rawTitle = (chat.title ?? '').trim()
  const fromMember = dialogMemberDisplayName(chat.dialog_member)
  const fromMembers = isDialog ? dialogPeerNameFromMembers(chat) : ''

  return [rawTitle, fromMember, fromMembers].filter(Boolean)
}

/** Лучшее имя: ФИО из title / dialog_member / Matrix, username и mxid слабее. */
export function pickRealChatTitle(...sources: ChatTitleSource[]): string {
  const chats = sources.filter(Boolean)
  if (chats.some(chat => chat.type === CHAT_TYPES.FAVORITE || (chat.title ?? '').trim() === 'Избранное')) {
    return FAVORITE_CHAT_TITLE
  }

  let best = ''
  let bestScore = 0
  for (const chat of chats) {
    for (const candidate of titleCandidatesFromChat(chat)) {
      const score = scoreDialogTitle(candidate, chat)
      if (score > bestScore) {
        best = candidate
        bestScore = score
      }
    }
  }

  return best
}

/**
 * Полное ФИО для шапки диалога: больше частей имени побеждает
 * («Тестовый Тест Михаилович» важнее «Тестовый Тест»).
 */
export function pickFullerPersonTitle(...sources: ChatTitleSource[]): string {
  const chats = sources.filter(Boolean)
  if (chats.some(chat => chat.type === CHAT_TYPES.FAVORITE || (chat.title ?? '').trim() === 'Избранное')) {
    return FAVORITE_CHAT_TITLE
  }

  let best = ''
  let bestScore = 0
  let bestParts = 0
  for (const chat of chats) {
    for (const candidate of titleCandidatesFromChat(chat)) {
      const score = scoreDialogTitle(candidate, chat)
      const parts = personNamePartCount(candidate)
      if (
        score > bestScore
        || (score === bestScore && parts > bestParts)
        || (score === bestScore && parts === bestParts && candidate.length > best.length)
      ) {
        best = candidate
        bestScore = score
        bestParts = parts
      }
    }
  }

  return best
}

/** Список чатов: диалог — фамилия+имя; группа/канал — обычный title. */
export function resolveChatDisplayTitle(chat: ChatTitleSource): string {
  if (chat.type === CHAT_TYPES.FAVORITE || (chat.title ?? '').trim() === 'Избранное') {
    return FAVORITE_CHAT_TITLE
  }

  if (chat.type === CHAT_TYPES.DIALOG || chat.type === CHAT_TYPES.DIRECT) {
    const listName = dialogMemberListName(chat.dialog_member)
    if (
      listName
      && !isNonHumanDialogTitle(
        listName,
        chat.matrix_room_id,
        chat.matrix_peer_id ?? chat.dialog_member?.matrix_user_id,
      )
    ) {
      return listName
    }
  }

  return pickRealChatTitle(chat) || 'Неизвестный'
}

/** Шапка открытого диалога: полное ФИО с отчеством, если есть. */
export function resolveChatHeaderTitle(chat: ChatTitleSource): string {
  if (chat.type === CHAT_TYPES.FAVORITE || (chat.title ?? '').trim() === 'Избранное') {
    return FAVORITE_CHAT_TITLE
  }

  if (chat.type === CHAT_TYPES.DIALOG || chat.type === CHAT_TYPES.DIRECT) {
    return pickFullerPersonTitle(chat) || resolveChatDisplayTitle(chat)
  }

  return resolveChatDisplayTitle(chat)
}

/** Событие Matrix: собеседник просит удалить чат у обоих */
export const MATRIX_CHAT_DELETE_FOR_PEER_EVENT = 'ru.daoit.chat.delete_for_peer'

/** Актуальный state event Matrix: общие метаданные backend-чата. */
export const MATRIX_ROOM_META_STATE_TYPE = 'ru.daoit.room.meta'

/** Legacy state event Matrix: метаданные чата задачи (event_url, chat_id). */
export const MATRIX_EVENT_ROOM_STATE_TYPE = 'ru.daoit.chat.event'

/** Room account data: закреп только для меня (диалог, как в Telegram) */
export const MATRIX_PERSONAL_PINNED_ACCOUNT_TYPE = 'ru.daoit.chat.personal_pinned'

/** Маркер m.notice о закрепе с уведомлением участников */
export const MATRIX_PIN_NOTICE_CONTENT_KEY = 'ru.daoit.chat.pin_notice'

export const MATRIX_FORWARD_CONTENT_KEY = 'ru.daoit.forward'

/** Кто поставил shared-pin: map event_id → mxid в content m.room.pinned_events */
export const MATRIX_PINNED_BY_CONTENT_KEY = 'ru.daoit.chat.pinned_by'

export type ChatType = (typeof CHAT_TYPES)[keyof typeof CHAT_TYPES]

/** Групповые типы (group/channel/workgroup) — общий UX управления участниками и темами */
const GROUP_LIKE_CHAT_TYPES = new Set<string>([
  CHAT_TYPES.GROUP,
  CHAT_TYPES.CHANNEL,
  CHAT_TYPES.WORKGROUP,
])

export function isGroupLikeChatType(type: string | undefined | null): boolean {
  return Boolean(type) && GROUP_LIKE_CHAT_TYPES.has(type as string)
}

/** Человекочитаемый тип группового чата для заголовков */
export function groupChatTypeLabel(type: string | undefined | null): string {
  if (type === CHAT_TYPES.CHANNEL) {
    return 'Канал'
  }
  if (type === CHAT_TYPES.WORKGROUP) {
    return 'Рабочая группа'
  }
  return 'Группа'
}

export const CHAT_LIST_TAB = {
  EMPLOYEES: 'employees',
  EVENTS: 'events',
} as const

export type ChatListTab = (typeof CHAT_LIST_TAB)[keyof typeof CHAT_LIST_TAB]

export const CHAT_LIST_TAB_LABELS: Record<ChatListTab, string> = {
  [CHAT_LIST_TAB.EMPLOYEES]: 'Чаты',
  [CHAT_LIST_TAB.EVENTS]: 'Чаты задач',
}

/** Параметр types для GET /api/matrix_chat/chat/ по вкладке */
export const CHAT_LIST_TYPES: Record<ChatListTab, string> = {
  [CHAT_LIST_TAB.EMPLOYEES]: 'dialog,group,channel,workgroup,favorite',
  [CHAT_LIST_TAB.EVENTS]: 'event',
}

export interface ChatListTabItem {
  id?: number | string
  type?: string
  event_url?: string | null
}

/** Event-чат: по type или по наличию event_url (на случай неполного ответа списка) */
export function isEventChatItem(chat: ChatListTabItem | undefined | null): boolean {
  if (!chat) {
    return false
  }

  return chat.type === CHAT_TYPES.EVENT || Boolean(chat.event_url)
}

/** Чат подходит под выбранную вкладку списка */
export function matchesChatListTab(
  chat: ChatListTabItem | undefined | null,
  tab: ChatListTab,
): boolean {
  const isEvent = isEventChatItem(chat)

  if (tab === CHAT_LIST_TAB.EVENTS) {
    return isEvent
  }

  return !isEvent
}

export function filterChatsByListTab<T extends ChatListTabItem>(
  chats: T[],
  tab: ChatListTab,
): T[] {
  return chats.filter(chat => matchesChatListTab(chat, tab))
}

export function sumChatsUnread(chats: Array<{ unread_count?: number }>): number {
  return chats.reduce((total, chat) => total + (chat.unread_count || 0), 0)
}

export function chatListTabFromTypes(types: string | null | undefined): ChatListTab {
  return types === CHAT_LIST_TYPES[CHAT_LIST_TAB.EVENTS]
    ? CHAT_LIST_TAB.EVENTS
    : CHAT_LIST_TAB.EMPLOYEES
}

const EVENT_PLACEHOLDER_TITLES = new Set(['empty room', 'empty_room', 'empty', 'неизвестный', 'unknown'])

/** Matrix-заглушка event-чата: room id, empty room, пустой title. */
export function isFallbackEventChatTitle(
  chat: Pick<IChat, 'title' | 'matrix_room_id'>,
): boolean {
  const title = chat.title?.trim()
  if (!title) {
    return true
  }
  if (chat.matrix_room_id && title === chat.matrix_room_id) {
    return true
  }
  if (EVENT_PLACEHOLDER_TITLES.has(title.toLowerCase())) {
    return true
  }
  if (title.startsWith('!') && title.includes(':')) {
    return true
  }
  return false
}

/** REST title важнее Matrix room name; заглушки (!roomId, empty room) пропускаем. */
export function pickEventChatTitle(
  ...sources: Array<Pick<IChat, 'title' | 'matrix_room_id'>>
): string {
  for (const source of sources) {
    const title = source.title?.trim()
    if (title && !isFallbackEventChatTitle({ ...source, title })) {
      return title
    }
  }

  return sources.find(source => source.title?.trim())?.title?.trim() || ''
}

/** Сохраняет event_url и type при слиянии краткого списка с детальными данными */
export function mergeEventChatFields<T extends ChatListTabItem>(
  chat: T,
  ...fallbacks: Array<ChatListTabItem | null | undefined>
): T {
  const nonEventTypes = new Set<string>([
    CHAT_TYPES.DIALOG,
    CHAT_TYPES.DIRECT,
    CHAT_TYPES.GROUP,
    CHAT_TYPES.CHANNEL,
    CHAT_TYPES.WORKGROUP,
    CHAT_TYPES.SPACE,
    'favorite',
  ])

  // Обычный чат — не переносим event_url с другого чата
  if (chat.type && nonEventTypes.has(chat.type)) {
    return chat
  }

  if (!isEventChatItem(chat)) {
    return chat
  }

  const relevantFallbacks = fallbacks.filter(
    (source): source is ChatListTabItem => {
      if (!source) {
        return false
      }

      if (source.id != null && chat.id != null && source.id !== chat.id) {
        return false
      }

      return isEventChatItem(source)
    },
  )

  const eventUrl = chat.event_url
    ?? relevantFallbacks.find(source => source.event_url)?.event_url
    ?? null

  return {
    ...chat,
    type: CHAT_TYPES.EVENT,
    event_url: eventUrl,
  }
}

/** Убирает event-комнаты из Matrix-списка «Чаты». */
export function excludeEventMatrixRooms(
  matrixChats: IChat[],
  restEventChats: IChat[],
): IChat[] {
  const eventRoomIds = new Set(
    restEventChats
      .map(chat => chat.matrix_room_id)
      .filter((roomId): roomId is string => Boolean(roomId)),
  )

  return matrixChats.filter(chat =>
    !chat.matrix_room_id || !eventRoomIds.has(chat.matrix_room_id),
  )
}

/** Есть текст или вложение — иначе в списке будет «Нет сообщений». */
export function hasLastMessagePreview(
  message?: { content?: string | null; attachments?: unknown[] | null } | null,
): boolean {
  if (!message) {
    return false
  }

  return Boolean(message.content?.trim() || (Array.isArray(message.attachments) && message.attachments.length > 0))
}

/** Берём более свежий last_message. При равном времени оставляем `rest` — иначе превью скачет между backend/Matrix. */
export function pickFresherLastMessage(rest: IChat, matrix: IChat): Pick<IChat, 'last_message' | 'last_message_id'> {
  const restPreview = hasLastMessagePreview(rest.last_message)
  const matrixPreview = hasLastMessagePreview(matrix.last_message)
  const restTime = Date.parse(rest.last_message?.created_at ?? '')
  const matrixTime = Date.parse(matrix.last_message?.created_at ?? '')

  if (matrixPreview && (!restPreview || (!Number.isNaN(matrixTime) && (Number.isNaN(restTime) || matrixTime > restTime)))) {
    return {
      last_message: matrix.last_message,
      last_message_id: matrix.last_message_id ?? matrix.last_message?.id,
    }
  }

  return {
    last_message: restPreview ? rest.last_message : matrix.last_message,
    last_message_id: restPreview
      ? (rest.last_message_id ?? rest.last_message?.id ?? matrix.last_message_id)
      : (matrix.last_message_id ?? matrix.last_message?.id ?? rest.last_message_id),
  }
}

/** Первый непустой URL иконки/аватара (backend часто отдаёт avatar_url: ""). */
export function resolveChatIcon(
  ...candidates: Array<string | null | undefined>
): string | null {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return null
}

/** Аватар чата из полей backend list/detail (Swagger) */
export function resolveChatAvatarFromIChat(chat: Pick<IChat, 'icon' | 'display_avatar_url' | 'avatar_url' | 'dialog_member'>): string | null {
  return resolveChatIcon(
    chat.icon,
    chat.display_avatar_url,
    chat.avatar_url,
    chat.dialog_member?.avatar,
  )
}

/**
 * Счётчик участников: primary (backend REST или /members) важнее fallback (Matrix room).
 * Matrix getJoinedMemberCount часто завышен после kick, пока Synapse не синхронизировался.
 */
export function pickTotalMembers(
  primary?: number | null,
  fallback?: number | null,
): number | undefined {
  if (typeof primary === 'number' && primary >= 0) {
    return primary
  }

  if (typeof fallback === 'number' && fallback >= 0) {
    return fallback
  }

  return undefined
}

/**
 * При слиянии версий чата берём max: GET /chat из‑за JOIN members часто отдаёт 1,
 * а /members.count — реальное число. Не даём урезанному значению затереть известное.
 */
export function preserveTotalMembers(
  ...values: Array<number | null | undefined>
): number | undefined {
  let max: number | undefined
  for (const value of values) {
    if (typeof value !== 'number' || value < 0) {
      continue
    }
    max = max == null ? value : Math.max(max, value)
  }
  return max
}

/** Сохраняет presence/runtime-поля при silent refresh списка чатов */
export function preserveChatRuntimeFields(previous: IChat, merged: IChat): IChat {
  return {
    ...merged,
    matrix_peer_id: merged.matrix_peer_id ?? previous.matrix_peer_id,
    online: merged.online ?? previous.online,
    last_seen_at: merged.last_seen_at ?? previous.last_seen_at,
    is_pinned: merged.is_pinned ?? previous.is_pinned,
    pinned_at: merged.pinned_at ?? previous.pinned_at ?? null,
    dialog_member: merged.dialog_member ?? previous.dialog_member,
  }
}

/** Backend-чат + Matrix room: backend — id/type/title, Matrix — last_message/unread. */
export function mergeBackendChatWithMatrix(backend: IChat, matrix: IChat): IChat {
  const lastMessageFields = pickFresherLastMessage(backend, matrix)

  if (isEventChatItem(backend) || isEventChatItem(matrix)) {
    return mergeRestEventChatWithMatrix(backend, matrix)
  }

  // roomToChat.created_at = getLastActiveTimestamp(); без last_message сортируем по активности Matrix, не по дате создания в backend.
  const hasPreview = hasLastMessagePreview(lastMessageFields.last_message)
  const backendCreatedMs = Date.parse(backend.created_at ?? '')
  const matrixActiveMs = Date.parse(matrix.created_at ?? '')
  const created_at = !hasPreview
    && !Number.isNaN(matrixActiveMs)
    && (Number.isNaN(backendCreatedMs) || matrixActiveMs > backendCreatedMs)
    ? (matrix.created_at ?? backend.created_at)
    : backend.created_at

  return {
    ...backend,
    ...lastMessageFields,
    created_at,
    // Диалог: в title полное ФИО (шапка); список берёт фамилию+имя из dialog_member.
    title: (backend.type === CHAT_TYPES.DIALOG || backend.type === CHAT_TYPES.DIRECT)
      ? (pickFullerPersonTitle(backend, matrix) || pickRealChatTitle(backend, matrix) || '')
      : (pickRealChatTitle(backend, matrix) || ''),
    matrix_room_id: backend.matrix_room_id ?? matrix.matrix_room_id,
    unread_count: matrix.unread_count ?? backend.unread_count ?? 0,
    online: matrix.online ?? backend.online,
    matrix_peer_id: backend.matrix_peer_id
      ?? matrix.matrix_peer_id
      ?? backend.dialog_member?.matrix_user_id
      ?? matrix.dialog_member?.matrix_user_id,
    // Для групп Matrix getJoinedMemberCount часто = 1 (space/general), не смешиваем.
    total_members: isGroupLikeChatType(backend.type)
      ? pickTotalMembers(backend.total_members)
      : pickTotalMembers(backend.total_members, matrix.total_members),
    icon: resolveChatIcon(
      backend.icon,
      backend.display_avatar_url,
      backend.avatar_url,
      backend.dialog_member?.avatar,
      matrix.icon,
    ),
    dialog_member: backend.dialog_member ?? matrix.dialog_member,
    display_avatar_url: backend.display_avatar_url ?? matrix.display_avatar_url,
    discussion_mode: backend.discussion_mode ?? matrix.discussion_mode,
    topics_display_mode: backend.topics_display_mode ?? matrix.topics_display_mode,
    transition_status: backend.transition_status ?? matrix.transition_status,
    space_room_id: backend.space_room_id ?? matrix.space_room_id,
    topics: backend.topics?.length ? backend.topics : (matrix.topics ?? backend.topics),
    capabilities: backend.capabilities ?? matrix.capabilities,
    // Не переносим Matrix space-parent на backend-группу
    parent_space_id: backend.parent_space_id,
  }
}

export function mergeBackendChatsWithMatrix(
  backendChats: IChat[],
  matrixChats: IChat[],
): IChat[] {
  const matrixByRoomId = new Map(
    matrixChats
      .filter(chat => chat.matrix_room_id)
      .map(chat => [chat.matrix_room_id!, chat] as const),
  )

  const merged = backendChats.map(backendChat => {
    if (!backendChat.matrix_room_id) {
      return {
        ...mergeEventChatFields(backendChat),
        title: pickRealChatTitle(backendChat) || '',
      }
    }

    const matrixChat = matrixByRoomId.get(backendChat.matrix_room_id)
    return matrixChat
      ? mergeBackendChatWithMatrix(backendChat, matrixChat)
      : {
          ...mergeEventChatFields(backendChat),
          title: pickRealChatTitle(backendChat) || '',
        }
  })

  return merged.sort(
    (left, right) => Date.parse(right.last_message?.created_at ?? right.created_at)
      - Date.parse(left.last_message?.created_at ?? left.created_at),
  )
}

/** REST event-чат + Matrix room: REST id/type/event_url, Matrix — активность и unread. */
export function mergeRestEventChatWithMatrix(rest: IChat, matrix: IChat): IChat {
  const lastMessageFields = pickFresherLastMessage(rest, matrix)

  return mergeEventChatFields({
    ...matrix,
    ...rest,
    ...lastMessageFields,
    id: rest.id,
    type: CHAT_TYPES.EVENT,
    matrix_room_id: rest.matrix_room_id ?? matrix.matrix_room_id,
    event_url: rest.event_url ?? matrix.event_url ?? null,
    title: pickEventChatTitle(rest, matrix),
    description: rest.description ?? matrix.description ?? '',
    icon: resolveChatIcon(rest.icon, matrix.icon),
    total_members: pickTotalMembers(rest.total_members),
    unread_count: matrix.unread_count ?? rest.unread_count ?? 0,
  })
}

export function mergeRestEventChatsWithMatrix(
  restEventChats: IChat[],
  matrixChats: IChat[],
): IChat[] {
  const matrixByRoomId = new Map(
    matrixChats
      .filter(chat => chat.matrix_room_id)
      .map(chat => [chat.matrix_room_id!, chat] as const),
  )

  return restEventChats.map(restChat => {
    if (!restChat.matrix_room_id) {
      return mergeEventChatFields(restChat)
    }

    const matrixChat = matrixByRoomId.get(restChat.matrix_room_id)
    return matrixChat
      ? mergeRestEventChatWithMatrix(restChat, matrixChat)
      : mergeEventChatFields(restChat)
  })
}

export function preserveEventChatMetadata(previous: IChat, fetched: IChat): IChat {
  if (!isEventChatItem(previous) && !isEventChatItem(fetched)) {
    return fetched
  }

  const merged = mergeEventChatFields(fetched, previous)

  return {
    ...merged,
    ...pickFresherLastMessage(previous, fetched),
    id: previous.id || fetched.id,
    matrix_room_id: fetched.matrix_room_id ?? previous.matrix_room_id,
    title: pickEventChatTitle(fetched, previous),
    event_url: fetched.event_url ?? previous.event_url ?? null,
  }
}

/**
 * Сливает Matrix-список с event-чатами из store (созданными на формах НС/заявок).
 * Без этого fetchChats затирает чаты, у которых в комнате ещё нет state `ru.daoit.chat.event`.
 */
export function mergeMatrixChatsWithEntityEventChats(
  matrixChats: IChat[],
  storedChats: IChat[],
): IChat[] {
  const eventChats = storedChats.filter(isEventChatItem)
  if (!eventChats.length) {
    return matrixChats
  }

  const mergedIds = new Set<number>()
  const mergedRoomIds = new Set<string>()

  const merged = matrixChats.map(matrixChat => {
    const eventSource = eventChats.find(eventChat => {
      if (eventChat.matrix_room_id && matrixChat.matrix_room_id) {
        return eventChat.matrix_room_id === matrixChat.matrix_room_id
      }

      return eventChat.id === matrixChat.id
    })

    if (!eventSource) {
      return matrixChat
    }

    const result = mergeRestEventChatWithMatrix(eventSource, matrixChat)
    mergedIds.add(result.id)
    if (result.matrix_room_id) {
      mergedRoomIds.add(result.matrix_room_id)
    }

    return result
  })

  for (const eventChat of eventChats) {
    if (mergedIds.has(eventChat.id)) {
      continue
    }

    if (eventChat.matrix_room_id && mergedRoomIds.has(eventChat.matrix_room_id)) {
      continue
    }

    merged.push(mergeEventChatFields(eventChat))
    mergedIds.add(eventChat.id)
    if (eventChat.matrix_room_id) {
      mergedRoomIds.add(eventChat.matrix_room_id)
    }
  }

  return merged.sort(
    (left, right) => Date.parse(right.last_message?.created_at ?? right.created_at)
      - Date.parse(left.last_message?.created_at ?? left.created_at),
  )
}

/**
 * Все константы чата
 */
export const CHAT_CONSTANTS = {
  MESSAGE_UPDATE_DELAY,
  REACTION_PICKER_HIDE_DELAY,
  REACTION_TRIGGER_HIDE_DELAY,
  CHAT_TYPES,
} as const
